import { memo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { FeatureData, MassSpectrumData } from "@/types/spectrum";
import { compactIntensity } from "@/utils/compactIntensity";
import { formatRt } from "@/utils/formatRt";
import { panelCls } from "@/utils/panelCls";

interface MassSpectrumProps {
  massSpectrumData: MassSpectrumData[];
  selectedFeature: string | null;
  features: FeatureData[];
  selectedRT?: number | null;
  isMsMissing?: boolean;
}

const MassSpectrum = ({
  massSpectrumData,
  selectedFeature,
  features,
  selectedRT,
  isMsMissing = false,
}: MassSpectrumProps) => {
  // T1 리뷰: 선택 RT 는 후보 클릭과 별개로 TIC/MZRT 클릭으로도 바뀐다. "AI 동정
  // 후보·비교·순위와 같은 스캔" 은 선택 후보의 RT 와 실제로 같을 때만 말한다.
  const selectedFeatureData = selectedFeature
    ? features.find((f) => f.feature_id === selectedFeature)
    : undefined;
  const sameAsSelectedCandidate =
    selectedFeatureData != null &&
    selectedRT != null &&
    Math.abs(selectedFeatureData.rt - selectedRT) < 1e-6;
  if (isMsMissing) {
    return (
      <div className={panelCls("standard", "h-full")}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center flex-wrap">
          <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
          <span>Mass Spectrum</span>
          <span className="ml-2 text-sm font-normal text-gray-500">
            (질량스펙트럼)
          </span>
        </h3>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            MS 미수집 — 질량 스펙트럼 없음. TIC만 이용 가능
          </p>
        </div>
      </div>
    );
  }

  // **"스캔 RT" 라고 부르지 않는다** (2026-09-06).
  //
  // 화재 시료의 MS 블록은 단일 스캔이 아니라 **여러 스캔의 평균**이다
  // (계측기 헤더 `+EI Scan (rt: 2.099-2.147 min, 9 scans)`. 화재 블록
  // 11,403개 중 11,402개가 평균, 스캔 6~26개). 우리가 쓰는 RT 는 그 구간의
  // **중점**이고(`import_fire_gcms_data.py:84-88`), 한 실행에서 60블록 중
  // 36개는 그 중점에 실제 스캔이 없다. 평균 구간 폭은 최대 1.253분이다.
  //
  // 그러므로 이 값을 "스캔 RT" 라고 부르면 **없는 스캔을 지목하는 문장**이
  // 된다. RT 자체는 사실이므로 RT 라고만 부른다. "N스캔 평균 (RT a~b분)" 로
  // 정직하게 쓰려면 API 가 scan_count·rt_start·rt_end 를 돌려줘야 한다
  // (현재 MassSpectrumWrapper 는 spectrum + scan_rt 뿐) — 별건.
  //
  // T1: 이전에는 번들된 시연용 D1-cotton CSV 의 최근접 스캔 RT 를 "실측 RT" 로
  // 같이 보여줬다 — 현재 시료와 무관한 제3의 RT 였다(분석 302 에서 선택 7.171 →
  // "실측 4.659"). 선택 RT 는 후보 클릭·TIC 클릭·MZRT 클릭 모두 실제 스캔 RT 이므로
  // 그 값 하나만 보여준다. 백엔드 mass-spectrum 은 이 RT 의 최근접 스캔을 돌려준다.
  return (
    <div className={panelCls("standard", "h-full")}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center flex-wrap">
        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
        <span>Mass Spectrum</span>
        <span className="ml-2 text-sm font-normal text-gray-500">
          (질량스펙트럼)
        </span>
        {selectedRT != null && (
          <span className="ml-2 text-sm text-gray-600 font-normal">
            (RT {formatRt(selectedRT)} min)
          </span>
        )}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        {/* margin.bottom 이 없으면 아래 x축 라벨이 SVG 밖에 그려져 보이지 않는다
            (2026-09-06 실측: viewBox 300 안에서 y=300 + dy 0.71em). */}
        <BarChart
          data={massSpectrumData}
          margin={{ top: 5, right: 5, bottom: 20, left: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="mz"
            type="number"
            domain={[0, "dataMax + 10"]}
            label={{
              value: "m/z",
              position: "insideBottom",
              offset: -8,
              style: { textAnchor: "middle", fill: "#64748b" },
            }}
            tick={{ fontSize: 12, fill: "#64748b" }}
          />
          <YAxis
            // 옆 TIC 과 같은 표기 (2026-09-04 에 TIC 에만 적용돼 있었다).
            // 7:5 배분으로 이 패널이 더 좁아져 600000 같은 눈금이 축 이름을
            // 덮을 여지가 커졌다. **줄이는 것은 눈금뿐이고**, 툴팁이 읽는
            // 값은 원래 숫자 그대로 둔다 (compactIntensity.ts 의 원칙).
            tickFormatter={compactIntensity}
            label={{
              value: "Intensity (counts)",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle", fill: "#64748b" },
            }}
            tick={{ fontSize: 12, fill: "#64748b" }}
          />
          <Tooltip
            formatter={(value) => [value, "Intensity"]}
            labelFormatter={(label) => `m/z: ${label}`}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
            }}
          />
          <Bar
            dataKey="intensity"
            fill="#f97316"
            radius={[2, 2, 0, 0]}
            barSize={10}
            isAnimationActive={false}
          >
            {/* 같은 m/z 가 여러 번 오면 key 가 충돌한다(실브라우저 콘솔
                오류로 확인 — jsdom 테스트는 잡지 못했다). */}
            {massSpectrumData.map((entry, index) => (
              <Cell key={`cell-${entry.mz}-${index}`} fill="#f97316" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {selectedRT != null && (
        <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
          {/* 2026-09-06: 문구를 줄였다(운영자: "핵심만 간결하게"). 세 갈래의
              **뜻**은 그대로다 — ① 선택 후보와 같은 스캔 ② 다른 스캔이라 아래
              비교·순위의 기준이 다르다 ③ 후보가 기록되지 않은 스캔. ②는 오독
              하면 결론이 바뀌므로 줄이되 지우지 않았다. */}
          선택 RT <strong>{formatRt(selectedRT)} min</strong>
          {selectedFeatureData
            ? sameAsSelectedCandidate
              ? ` · 선택 후보 ${selectedFeatureData.feature_id} 의 스캔`
              : ` · 차트에서 고른 스캔 — 아래 비교·순위는 선택 후보 ${selectedFeatureData.feature_id}(RT ${formatRt(selectedFeatureData.rt)}) 기준입니다`
            : " · AI 후보가 기록되지 않은 스캔"}
        </div>
      )}

      {!selectedRT && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded text-center">
          <p className="text-sm text-gray-500">RT를 선택하세요.</p>
        </div>
      )}
    </div>
  );
};

export default memo(MassSpectrum);
